import {inject, Injectable, signal} from '@angular/core';
import {MsalService} from '@azure/msal-angular';
import {environment} from '../../../environments/environment';
import {AccountInfo, InteractionRequiredAuthError} from '@azure/msal-browser';
import {LoginUser} from '../../models/users/loginUser';
import {UserApiService} from '../../services/user-api-service';
import {Router} from '@angular/router';
import {LoginUserCommand} from '../../models/users/loginUserCommand';
import {toObservable} from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly msalService = inject(MsalService);
  private readonly userApiService = inject(UserApiService);
  private readonly router = inject(Router);

  accessToken = signal<string | null>(null);
  loginUser = signal<LoginUser | null>(null);

  public backendLogin = signal<boolean>(false);
  public backendLogin$ = toObservable(this.backendLogin);

  reset(): void {
    this.accessToken.set(null);
    this.loginUser.set(null);
  }

  logout() {
    this.reset();
    this.msalService.instance.initialize().then(() => {
      this.msalService.instance.setActiveAccount(null);
      this.msalService.instance.logoutRedirect();
    });
  }

  login(redirect: string) {
    this.backendLogin.set(true);

    this.msalService.instance.handleRedirectPromise()
      .then(() => {
        this.setAccessToken(redirect);
      })
      .catch(() => {
        this.backendLogin.set(false);
      });
  }

  isAlreadyLoggedIn(): boolean {
    const accounts = this.msalService.instance.getAllAccounts();
    return accounts.length > 0;
  }

  setAccessToken(redirect: string): void {
    const accounts = this.msalService.instance.getAllAccounts();

    if (accounts.length === 0) {
      this.msalService.loginRedirect();
    }
    else {
      const silentRequest = {
        account: accounts[0],
        scopes: ['openid', environment.msalConfig.defaultScopeName]
      };

      this.msalService.instance
        .acquireTokenSilent(silentRequest)
        .then(response => {
          this.msalService.instance.setActiveAccount(accounts[0]);
          this.accessToken.set(response.accessToken);
          this.redirect(redirect, accounts[0]);
        })
        .catch(error => {
          if (error instanceof InteractionRequiredAuthError) {
            this.msalService.instance.acquireTokenRedirect(silentRequest);
          } else {
            this.router.navigate(['/unauthorized']);
          }
        });
    }
  }

  redirect(redirect: string, accountInfo: AccountInfo): void {
    const loginUserCommand: LoginUserCommand = {
      id: accountInfo.localAccountId,
      mail: this.getMail(accountInfo),
      givenName: accountInfo.idTokenClaims?.['given_name'] as string ?? '',
      lastName: accountInfo.idTokenClaims?.['last_name'] as string ?? '',
      role: this.getRole(accountInfo)
    }

    this.userApiService.add(loginUserCommand)
      .subscribe({
        next: loginResult => {
          if (loginResult.success) {
            this.setLoginUser(accountInfo, loginResult.tenantName, loginResult.isAdmin);
            this.backendLogin.set(false);

            if (loginResult.isAdmin) {
              this.router.navigate([this.isLoginRedirect(redirect) ? '/dashboard' : redirect]);
            } else {
               this.router.navigate([this.isLoginRedirect(redirect) ? '/import' : redirect]);
            }
          }
          else {
            this.router.navigate(['/unauthorized']);
          }
        }
      });
  }

  isLoginRedirect(redirect: string): boolean {
    return redirect === '/' || redirect.includes('#state=');
  }

  verifyTenant(tenant?: string): boolean {
    if (tenant !== undefined && environment.tenants.includes(tenant.toLowerCase())) {
      return true;
    }
    return false;
  }

  getMail(accountInfo: AccountInfo): string {
    let mail = accountInfo.idTokenClaims?.['email'] as string ?? ''
    if (mail === '') {
      mail = accountInfo.idTokenClaims?.['emails']?.[0] ?? ''
    }

    return mail;
  }

  getRole(accountInfo: AccountInfo): string {
    const roles = accountInfo.idTokenClaims?.['role'] as string[];
    return roles?.[0] ?? ''
  }

  setLoginUser(accountInfo: AccountInfo, tenant: string, isAdmin: boolean): void {
    const mail = this.getMail(accountInfo);

    if (mail === '') {
      return;
    }

    this.loginUser.set({
      tenant: tenant,
      name: accountInfo.username ?? '',
      mail,
      id: accountInfo.localAccountId,
      isAdmin
    });
  }
}
